import React, {useEffect, useState} from 'react'
import {Alert, Backdrop, Button, CircularProgress, Link, TextField, Tooltip} from '@mui/material'
import {AuthChallenge, useAuth} from './auth'
import LoginIcon from '@mui/icons-material/Login'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {useLocation, useNavigate} from 'react-router-dom'
import {webEvents} from '../common/WebEvents'
import {APP_VERSION, WEBEVENTS_URI} from '../common/environment'

/* Displays the status of the process: such as error and success messages */
type ActionStatus = {
    message: string
    isError: boolean
}

const EMPTY_STATUS = {message: '', isError: false}
const EMPTY_CHALLENGE = {challengeId: '', expiresAt: new Date()}
const NEW_CODE_MESSAGE =
    'A new code has been successfully sent to your email. Disregard the old code.'

function Login() {
    const auth = useAuth()
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')

    /* ref to action button DOM element, so we can click() it on <ENTER> key */
    const actionButton = React.useRef(null)

    /* processing a request: such as h3 auth or token endpoint. page overlay/spinner displays when true */
    const [processing, setProcessing] = useState(false)

    /* error or success message, or EMPTY_STATUS (none) */
    const [actionStatus, setActionStatus] = useState<ActionStatus>(EMPTY_STATUS)

    /* auth challenge: which is presented to /iam/token endpoint */
    const [otp, setOtp] = useState('')
    const [authChallenge, setAuthChallenge] =
        useState<AuthChallenge>(EMPTY_CHALLENGE)

    const navigate = useNavigate()
    const location = useLocation()
    const {state} = location

    useEffect(() => {
        if (state?.message) {
            setActionStatus(state)
        }
    }, [location])

    async function login() {
        await loginWithChallenge()
    }

    async function loginWithChallenge(authChallengeId?: string) {
        try {
            setProcessing(true)
            setOtp('')
            setActionStatus(EMPTY_STATUS)

            let result = await auth.login(username, password, authChallengeId)

            if (typeof result === 'string') {
                loginComplete(result)
            } else {
                setAuthChallenge({expiresAt: result.expiresAt, challengeId: result.challengeId})
            }

            return true
        } catch (error: any) {
            setActionStatus({message: error.message, isError: true})
            setAuthChallenge(EMPTY_CHALLENGE)
            return false
        } finally {
            setProcessing(false)
        }
    }

    async function verify() {
        try {
            setProcessing(true)
            loginComplete(await auth.verify(authChallenge.challengeId, otp))
        } catch (error: any) {
            setActionStatus({message: error.message, isError: true})
        } finally {
            setProcessing(false)
        }
    }

    function loginComplete(h3token: string) {
        webEvents.connect(WEBEVENTS_URI, {version: APP_VERSION, h3token})
        const user = auth.getUser()

        switch (user.type) {
            case 'technician':
                if (!user.clocked_in) {
                    navigate('/clock-in')
                } else {
                    navigate(state?.path || '/')
                }
                break

            default:
                navigate(state?.path || '/')
                break
        }
    }

    async function sendNewCode() {
        if (await loginWithChallenge(authChallenge.challengeId))
            setActionStatus({message: NEW_CODE_MESSAGE, isError: false})
    }

    let action = authChallenge.challengeId ? 'verify' : 'login'
    let disableButton = !(action == 'login'
        ? hasValidCredentials()
        : hasValidOtp())

    // if (state.message)
    //     setActionStatus({message: state.message, isError: state.isError})

    /* first div serves as a positioning container, allowing us to center the form.
     * second div is the centered flex item and contains a header w/logo and form.
     */
    return (
        <div className="flex items-center justify-center h-screen w-screen">
            <div
                className="tg-white-smoke-bg w-11/12 max-w-md rounded-lg"
                style={{boxShadow: '0 0 3px 0 rgba(0,0,0,0.4)'}}>
                <header className="tg-jordy-blue-bg rounded-t-lg p-6">
                    <img
                        alt="HTL Logo"
                        src="/logo.svg"
                        className="w-2/4 relative top-px"
                    />
                </header>

                <form className="pb-8 px-8 pt-6">
                    <Backdrop open={processing} className="z-50">
                        <CircularProgress color="inherit"/>
                    </Backdrop>

                    <h1 className="text-2xl pb-4 font-medium">
                        Login to TechGo
                    </h1>
                    <Alert
                        variant="filled"
                        severity={'warning'}
                        className="mb-2">
                        Please use your HTL email and Portal password to login. OPENLANE Okta credentials will no longer work.
                    </Alert>

                    {action == 'login' && actionStatus.message && (
                        <Alert
                            variant="filled"
                            severity={
                                actionStatus.isError ? 'error' : 'success'
                            }
                            className="mb-2">
                            {actionStatus.message}
                        </Alert>
                    )}

                    <label htmlFor="email" className="text-xs">
                        <b>htl email (<i>@htlkeys.com)</i></b>
                    </label>
                    <TextField
                        type="text"
                        id="email"
                        disabled={action == 'verify'}
                        className="w-full"
                        sx={{mb: 1}}
                        size="small"
                        autoComplete="on"
                        onChange={e => setUsername(e.target.value)}
                        required
                    />

                    <label htmlFor="password" className="text-xs">
                        <b>portal password</b>
                    </label>
                    <TextField
                        type="password"
                        id="password"
                        disabled={action == 'verify'}
                        className="w-full"
                        sx={{mb: 4}}
                        size="small"
                        autoComplete="on"
                        onKeyDown={clickOnEnter}
                        onChange={e => setPassword(e.target.value)}
                        required
                    />

                    {action == 'verify' && (
                        <>
                            <h1 className="text-2xl pb-2/4 font-medium">
                                Authentication Challenge
                            </h1>
                            <p className="pb-2 text-sm tg-soft-text-color">
                                Enter the password sent to your email.
                            </p>
                            {action == 'verify' && actionStatus.message && (
                                <Alert
                                    variant="filled"
                                    severity={
                                        actionStatus.isError
                                            ? 'error'
                                            : 'success'
                                    }
                                    className="mb-2">
                                    {actionStatus.message}
                                </Alert>
                            )}

                            <label htmlFor="otp" className="text-xs">
                                <b>one-time password</b>
                            </label>
                            <TextField
                                type="text"
                                id="otp"
                                className="w-full"
                                size="small"
                                value={otp}
                                onKeyDown={clickOnEnter}
                                onChange={normalizeOtp}
                                required
                            />
                            <div className="flex pb-8 text-xs justify-between">
                                <Tooltip
                                    placement="bottom-start"
                                    title="Send a new code if this one timed out or you didn't receive it.">
                                    <Link
                                        className="cursor-pointer"
                                        onClick={sendNewCode}>
                                        Send New Code
                                    </Link>
                                </Tooltip>
                                <p className="tg-soft-text-color italic">
                                    Expires{' '}
                                    {authChallenge.expiresAt
                                        .toLocaleString()
                                        .replace(',', '')}
                                </p>
                            </div>
                        </>
                    )}

                    <Button
                        disabled={disableButton}
                        className="hover:opacity-80 text-center w-full"
                        type="button"
                        variant="contained"
                        ref={actionButton}
                        size="large"
                        onClick={action == 'verify' ? verify : login}>
                        {action == 'login' ? (
                            <LoginIcon className="mr-1"/>
                        ) : (
                            <VisibilityIcon className="mr-1"/>
                        )}{' '}
                        {action}
                    </Button>
                </form>
                <footer
                    style={{fontSize: '.65rem'}}
                    className="bg-black text-white px-4 lg:px-6 py-2">
                    v{APP_VERSION} &#x1F41D; High Tech Locksmiths &copy;{' '}
                    {new Date().getFullYear()}. All rights reserved.
                </footer>
            </div>
        </div>
    )

    function clickOnEnter(e: any) {
        if (e.key === 'Enter') (actionButton.current as any)?.click() // does nothing if disabled
    }

    function hasValidCredentials(): boolean {
        return (
            username.length >= 7 &&
            username.includes('@') &&
            password.length >= 6
        )
    }

    function hasValidOtp(): boolean {
        return otp.length == 8 // normalizeOtp does the rest
    }

    /* removes everything except letters and numbers. truncates to a length of 8. */
    function normalizeOtp(e: any) {
        setOtp(
            e.target.value
                .replace(/[^a-z0-9]/gi, '')
                .toUpperCase()
                .slice(0, 8)
        )
    }
}

export default Login
